import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckoutButton } from "../components/CheckoutButton";
import { useAuth } from "../auth/AuthContext";
import { DoramaCard } from "../components/DoramaCard";
import { FavoriteButton } from "../components/FavoriteButton";
import { ProtectedPlayer } from "../components/ProtectedPlayer";
import { Seo } from "../components/Seo";
import { categoryIcons, categoryName, formatPrice } from "../lib/catalog";
import { supabase } from "../lib/supabase";
import { buildBunnyPlayerUrl } from "../lib/video";
import type { Dorama } from "../types";

export function DoramaPage() {
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [dorama, setDorama] = useState<Dorama | null>(null);
  const [related, setRelated] = useState<Dorama[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [shareMessage, setShareMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    setLoading(true);
    setNotFound(false);
    setPreviewPlaying(false);
    const isId = /^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(slug);
    const query = supabase.from("doramas").select("*");
    (isId ? query.eq("id", slug) : query.eq("slug", slug))
      .maybeSingle()
      .then(async ({ data, error }) => {
        if (!active) return;
        if (error || !data) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        const item = data as Dorama;
        setDorama(item);
        const relatedResult = await supabase.from("doramas").select("*").eq("category", item.category).neq("id", item.id).eq("published", true).limit(6);
        if (active) setRelated((relatedResult.data || []) as Dorama[]);
        setLoading(false);
      });
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!dorama) {
      setHasAccess(false);
      setAccessLoading(false);
      return;
    }
    let active = true;
    setAccessLoading(true);
    supabase.from("dorama_media").select("dorama_id").eq("dorama_id", dorama.id).maybeSingle().then(({ data }) => {
      if (!active) return;
      setHasAccess(Boolean(data));
      setAccessLoading(false);
    });
    return () => { active = false; };
  }, [dorama, user]);

  if (loading) return <div className="page-container status-box" role="status">Carregando novelinha…</div>;
  if (notFound || !dorama) return <NotFoundContent />;

  const trailerUrl = buildBunnyPlayerUrl(dorama.trailer_video_id || dorama.trailer_url, { autoplay: true });


  async function share() {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: dorama!.title, text: `Veja ${dorama!.title} no DoramaFlix`, url });
      else {
        await navigator.clipboard.writeText(url);
        setShareMessage("Link copiado!");
      }
    } catch {
      setShareMessage("Não foi possível compartilhar agora.");
    }
  }

  return (
    <article className="dorama-detail-page">
      <Seo title={`${dorama.title} — assistir dublado e legendado | DoramaFlix`} description={dorama.description || `Assista ${dorama.title} no DoramaFlix.`} path={`/dorama/${dorama.slug || dorama.id}`} />
      <div className="detail-shell page-container">
        <Link className="back-link" to="/">← Voltar</Link>

        <div className="detail-preview-player">
          {previewPlaying && trailerUrl ? (
            <iframe src={trailerUrl} title={`Prévia de ${dorama.title}`} loading="eager" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
          ) : (
            <button type="button" onClick={() => trailerUrl && setPreviewPlaying(true)} aria-label={`Assistir prévia de ${dorama.title}`}>
              {(dorama.hero_image_url || dorama.cover_url) && <img src={dorama.hero_image_url || dorama.cover_url || ""} alt={`${dorama.title} — imagem da prévia`} />}
              <span className="detail-preview-play" aria-hidden="true">▶</span>
              <small>▶ Prévia</small>
            </button>
          )}
        </div>

        <Link className="detail-vip-banner" to="/vip"><span aria-hidden="true">♕</span><span><strong>Passe Vitalício — Assista TUDO</strong><small>Por apenas R$ 27 — uma vez, para sempre</small></span><b aria-hidden="true">✧</b></Link>

        <div className="detail-offer-grid">
          <section className="detail-copy">
            <p className="detail-category">{categoryIcons[dorama.category || ""] || "🎬"} {categoryName(dorama.category)}</p>
            <h1>{dorama.title}</h1>
            {dorama.original_title && <p className="original-title">{dorama.original_title}</p>}
            <div className="detail-meta"><span className="hero-rating">★ {dorama.rating ?? "—"}</span><span>•</span><span>{dorama.year ?? "—"}</span><span>•</span><span>{dorama.content_type === "complete" ? "Completo · Todos Episódios" : `${dorama.episodes ?? 0} episódios`}</span></div>
            <h2>Sinopse</h2>
            <p>{dorama.description || "Sinopse não informada."}</p>
            <div className="detail-actions">
              <FavoriteButton doramaId={dorama.id} />
              <div className="share-control"><button type="button" onClick={share}>↗ Compartilhar</button>{shareMessage && <small role="status">{shareMessage}</small>}</div>
            </div>
          </section>

          <aside className="detail-purchase-card">
            <span>{hasAccess ? "Conteúdo liberado" : "Assista agora"}</span>
            <strong>{hasAccess ? "✓" : formatPrice(dorama.price)}</strong>
            <small>{hasAccess ? "na sua conta" : "todos os episódios"}</small>
            {authLoading || accessLoading ? <button type="button" disabled>Verificando acesso…</button> : hasAccess ? <button type="button" onClick={() => document.getElementById("watch-title")?.scrollIntoView({ behavior: "smooth" })}>Assistir filme completo</button> : <CheckoutButton doramaId={dorama.id} price={dorama.price}>Desbloquear novela completa</CheckoutButton>}
            <ul>
              <li>Acesso a todos os {dorama.episodes ?? 0} episódios</li>
              <li>Qualidade Full HD</li>
              <li>{dorama.has_subtitles ? "Legendas em Português" : "Áudio em Português"}</li>
              <li>Sem anúncios</li>
            </ul>
          </aside>
        </div>

        {hasAccess && <ProtectedPlayer dorama={dorama} />}

        <section className="detail-information">
          <h2>Conteúdo completo</h2>
          <p className="detail-complete-badge">Completo · Todos Episódios</p>
          <p>Desbloqueie todos os episódios desta novela e assista quando quiser. O acesso fica salvo na sua conta.</p>
        </section>

        <section className="detail-information">
          <h2>Sobre {dorama.title}</h2>
          <p>{dorama.title} é uma novelinha de {categoryName(dorama.category).toLowerCase()} lançada em {dorama.year || "ano não informado"}, com {dorama.episodes || "vários"} episódios, disponível dublada{dorama.has_subtitles ? " e legendada" : ""}, em Full HD e sem anúncios.</p>
          <p>O acesso individual é um pagamento único de {formatPrice(dorama.price)}, sem assinatura mensal. O Passe Vitalício de R$ 27 libera todo o catálogo.</p>
        </section>

        <section className="detail-information detail-faq">
          <h2>Perguntas frequentes sobre {dorama.title}</h2>
          <details><summary>Onde assistir {dorama.title}?</summary><p>Você pode assistir no DoramaFlix pelo celular, tablet ou computador.</p></details>
          <details><summary>Quantos episódios estão disponíveis?</summary><p>O título possui {dorama.episodes || "todos os"} episódios, liberados de uma vez após o desbloqueio.</p></details>
          <details><summary>Preciso de assinatura?</summary><p>Não. O acesso individual e o Passe Vitalício são pagamentos únicos, sem renovação automática.</p></details>
        </section>

        {related.length > 0 && <section className="detail-related"><h2>Você também pode gostar</h2><div className="card-grid">{related.map((item) => <DoramaCard dorama={item} key={item.id} />)}</div></section>}
      </div>
    </article>
  );
}

function NotFoundContent() {
  return (
    <section className="not-found page-container">
      <p className="eyebrow">CONTEÚDO NÃO ENCONTRADO</p>
      <h1>Essa novelinha não está disponível</h1>
      <p>O endereço pode estar incorreto ou o conteúdo pode ter sido removido.</p>
      <Link className="primary-button" to="/">Voltar ao catálogo</Link>
    </section>
  );
}
